import { Button, IconButton, Menu, MoreVertIcon, Tooltip, Typography } from '@cmg/design-system';
import React from 'react';

import MenuItemWithDisabledTooltip, {
  type Props as MenuItemWithDisabledTooltipProps,
} from '../../../../../../../../design-system/components/MenuItemWithDisabledTooltip';
import { ButtonProps, MenuItemProps } from '../../types';

export type Props = Readonly<{
  baseButtonAccess: boolean;
  isVisible: {
    edit: boolean;
    pass: boolean;
    reactivate: boolean;
    cancel: boolean;
    delete?: boolean;
  };
  editButtonProps?: ButtonProps;
  menuItemProps: {
    pass?: MenuItemProps;
    reactivate?: MenuItemProps;
    cancel: MenuItemProps;
    delete?: MenuItemWithDisabledTooltipProps;
  };
  hasComplianceWarning?: boolean;
}>;

const COMPLIANCE_TOOLTIP = (
  <Typography variant="tooltip">
    To submit and modify indications, review the outstanding requirements in the Compliance section.{' '}
    <br />
    For support, contact your broker relationship(s).
  </Typography>
);

const complianceTooltipProps: {
  title: React.ReactNode;
  variant: 'info';
  placement: 'top';
} = {
  title: COMPLIANCE_TOOLTIP,
  variant: 'info',
  placement: 'top',
};

const ReadButtons: React.FC<Props> = ({
  baseButtonAccess,
  isVisible,
  editButtonProps,
  menuItemProps,
  hasComplianceWarning = false,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const btnRef = React.useRef<null | HTMLButtonElement>(null);

  if (!baseButtonAccess) {
    return null;
  }

  const buttons = [
    ...(isVisible.edit
      ? [
          hasComplianceWarning ? (
            <Tooltip variant="info" placement="top" title={COMPLIANCE_TOOLTIP}>
              <span>
                <Button variant="contained" {...editButtonProps} disabled>
                  Edit
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button key="edit" variant="contained" {...editButtonProps}>
              Edit
            </Button>
          ),
        ]
      : []),
    <IconButton
      key="menu-button"
      onClick={() => setIsExpanded(prev => !prev)}
      aria-label="more"
      aria-controls="menu-id"
      ref={btnRef}
    >
      <MoreVertIcon />
    </IconButton>,
    <Menu
      data-testid="more-actions-menu"
      key="menu"
      open={isExpanded}
      onClose={() => setIsExpanded(false)}
      id="menu-id"
      anchorEl={btnRef.current}
    >
      {isVisible.pass && (
        <MenuItemWithDisabledTooltip
          key="pass"
          {...menuItemProps.pass}
          disabled={hasComplianceWarning || menuItemProps.pass?.disabled}
          tooltipProps={hasComplianceWarning ? complianceTooltipProps : undefined}
          onClick={args => {
            setIsExpanded(false);
            menuItemProps.pass?.onClick?.(args);
          }}
        >
          Pass
        </MenuItemWithDisabledTooltip>
      )}
      {isVisible.reactivate && (
        <MenuItemWithDisabledTooltip
          key="reactivate"
          {...menuItemProps.reactivate}
          disabled={hasComplianceWarning || menuItemProps.reactivate?.disabled}
          tooltipProps={hasComplianceWarning ? complianceTooltipProps : undefined}
          onClick={args => {
            setIsExpanded(false);
            menuItemProps.reactivate?.onClick?.(args);
          }}
        >
          Re-activate
        </MenuItemWithDisabledTooltip>
      )}
      {isVisible.cancel && (
        <MenuItemWithDisabledTooltip
          key="cancel"
          {...menuItemProps.cancel}
          disabled={hasComplianceWarning || menuItemProps.cancel?.disabled}
          tooltipProps={hasComplianceWarning ? complianceTooltipProps : undefined}
          onClick={args => {
            setIsExpanded(false);
            menuItemProps.cancel?.onClick?.(args);
          }}
        >
          Cancel
        </MenuItemWithDisabledTooltip>
      )}
      {isVisible.delete && (
        <MenuItemWithDisabledTooltip
          key="delete"
          {...menuItemProps.delete}
          disabled={hasComplianceWarning || menuItemProps.delete?.disabled}
          tooltipProps={
            hasComplianceWarning ? complianceTooltipProps : menuItemProps?.delete?.tooltipProps
          }
          onClick={args => {
            setIsExpanded(false);
            menuItemProps.delete?.onClick?.(args);
          }}
        >
          Delete
        </MenuItemWithDisabledTooltip>
      )}
    </Menu>,
  ];

  return <React.Fragment>{...buttons}</React.Fragment>;
};

export default ReadButtons;
